import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, FormGroup, Label, Button } from 'reactstrap';
import { get, post, put } from 'helpers/requests';
import { BASE_URL, API } from 'CONST';
import '../../../styles/deudor.css';

import { formatRut, fdate } from '../../../helpers/helpers';

const PosicionDeudorInfo = ({ datos }) => {
  const [rubro, setRubro] = useState('---');
  const [localInicioActividad, setLocalInicioActividad] = useState(null);
  const [localActividades, setLocalActividades] = useState([]);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [grupoEconomico, setGrupoEconomico] = useState(null);

  const isChilean = !datos.pais || datos.pais.code === 'CL';

  useEffect(() => {
    setLocalInicioActividad(datos?.inicio_actividad ?? null);
  }, [datos?.inicio_actividad]);

  useEffect(() => {
    setLocalActividades(datos?.actividad || []);
  }, [datos?.actividad]);

  useEffect(() => {
    if (!datos?.rut) return;
    
    get(`${BASE_URL}${API.organization}/?rut=${datos.rut}`)
      .then(orgResponse => {
        if (orgResponse && orgResponse.results && orgResponse.results.length > 0) {
          const organization = orgResponse.results[0];
          if (organization.grupo && organization.grupo.nombre) {
            setGrupoEconomico(organization.grupo.nombre);
          } else {
            setGrupoEconomico(null);
          }
        }
      })
      .catch(error => {
        console.error('Error al obtener la organización:', error);
        setGrupoEconomico(null);
      });
  }, [datos?.rut]);

  useEffect(() => {
    if (datos && datos.rubro_id) {
      get(`${BASE_URL}${API.actividadEconomica}/${datos.rubro_id}`).then((response) => {
        setRubro(response.descripcion);
      });
    }
  }, [datos?.rubro_id]);

  const solicitarDataSii = useCallback(async () => {
    try {
      // Solo continuar si no hay pais o si el pais es Chile
      if (!datos || (datos.pais && datos.pais.code !== 'CL')) return;

      const body = { rut: datos.rut };
      const tamanoEmpresaOptions = await get(`${BASE_URL}${API.tamanoEmpresa}/?ordering=descripcion`);
      const response = await post(`${BASE_URL}${API.getDataSii}`, body);

      if (!response || response.inicio_actividades === null) return;

      if (response.actividades_economicas) {
        const actividades = response.actividades_economicas.map(a => ({
          actividad_economica: a.actividad_economica,
        }));        

        const tamanoEmpresaOption = tamanoEmpresaOptions.results.find(
          option => option.name === response.tamano_empresa
        ) || tamanoEmpresaOptions.results[0];

        //  const isPyme = response.es_pyme;

        //  const tamanoDisplayText = isPyme 
        //  ? `${tamanoEmpresaOption.name} (Es PYME)`
        //  : tamanoEmpresaOption.name;
        const tamanoDisplayText = tamanoEmpresaOption.name;
        const [day, month, year] = response.inicio_actividades.split('-');
        const formattedDate = `${year}-${month}-${day}`;

        const dataToUpdate = datos;
        dataToUpdate.tamano = tamanoDisplayText;
        dataToUpdate.inicio_actividad = formattedDate;
        dataToUpdate.actividad = actividades;

        // eslint-disable-next-line react/prop-types
        await put(`${BASE_URL}${API.deudor}/${datos.id}/`, dataToUpdate);

        setLocalInicioActividad(formattedDate);
        setLocalActividades(actividades);

      } else {
        console.error("No se encontró información de actividad económica.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [datos]);

  // Si inicio_actividades  es null, hace la solicitud al SII
  useEffect(() => {
    if (datos && (datos.inicio_actividad === null || datos.actividad === null || datos.tamano === null)) {
      solicitarDataSii();
    }
  }, [datos, solicitarDataSii]);

  const getInput = (input) => input || '---';

  return (
    <>
      <h5 className="deudor-title">Datos del Deudor</h5>
      <hr className="deudor-title-separator" />
      {!isChilean && (
        <FormGroup row>
          <Label sm={3} for="pais">
            País
          </Label>
          <p id="pais" className="form-group-value">{datos?.pais?.name}</p>
        </FormGroup>
      )}
      <FormGroup row>
        <Label sm={3} for="rutDeudor">
          {isChilean ? 'Rut' : 'N° Documento'}
        </Label>
        <p id="rutDeudor" className="form-group-value">
          {isChilean ? formatRut(datos.rut) : datos.rut}
        </p>
      </FormGroup>
      <FormGroup row style={{ flexWrap: 'nowrap' }}>
        <Label sm={3} for="razonSocial">
          Razón Social
        </Label>
        <p id="razonSocial" className="form-group-value">{datos.name}</p>
      </FormGroup>
      <FormGroup row style={{ flexWrap: 'nowrap' }}>
        <Label sm={3} for="grupoEconomico">
          Grupo Económico
        </Label>
        <p id="grupoEconomico" className="form-group-value">
          {grupoEconomico || '---'}
        </p>
      </FormGroup>
      <FormGroup row>
        <Label sm={3} for="fechaInicio">
          Fecha Inicio Actividades
        </Label>
        <p id="fechaInicio" className="form-group-value">{getInput(fdate(localInicioActividad))}</p>
      </FormGroup>
      <FormGroup row>
        <Label sm={3} for="actividad">
          Actividades
        </Label>
        <Col className="form-group-value">
          {localActividades.length > 0 ? (
            <div>
              <ul className="list-unstyled mb-1">
                {(showAllActivities ? localActividades : localActividades.slice(0, 1)).map((act) => (
                  <li key={act.codigo || act.actividad_economica} className="dot-item">
                    •
                    {' '} 
                    {act.actividad_economica}
                  </li>
                ))}
              </ul>
              {localActividades.length > 1 && (
                <Button 
                  color="link" 
                  size="sm" 
                  className="p-0 text-primary"
                  onClick={() => setShowAllActivities(prev => !prev)}
                >
                  {showAllActivities ? 
                    'Mostrar menos' : 
                    `Mostrar ${localActividades.length - 1} más`}
                </Button>
              )}
            </div>
          ) : (
            '---'
          )}
        </Col>
      </FormGroup>
      <FormGroup row style={{ flexWrap: 'nowrap' }}>
        <Label sm={3} for="rubro">
          Rubro
        </Label>
        <p id="rubro" className="form-group-value">{rubro}</p>
      </FormGroup>
      <FormGroup row>
        <Label sm={3} for="tamanno">
          Tamaño
        </Label>
        <p id="tamanno" className="form-group-value">{getInput(datos.tamano)}</p>
      </FormGroup>
      <FormGroup row style={{ flexWrap: 'nowrap' }}>
        <Label sm={3} for="nombreContacto">
          Nombre Contacto
        </Label>
        <p id="nombreContacto" className="form-group-value">{getInput(datos.contacto)}</p>
      </FormGroup>
      <FormGroup row>
        <Label sm={3} for="emailContacto">
          Email Contacto
        </Label>
        <p id="emailContacto" className="form-group-value">{getInput(datos.email)}</p>
      </FormGroup>
      <FormGroup row>
        <Label sm={3} for="telefonoContacto">
          Teléfono Contacto
        </Label>
        <p id="telefonoContacto" className="form-group-value">{getInput(datos.phone)}</p>
      </FormGroup>
    </>
  );
};

PosicionDeudorInfo.propTypes = {
  datos: PropTypes.shape({
    rut: PropTypes.string,
    name: PropTypes.string,
    estado: PropTypes.string,
    inicio_actividad: PropTypes.string,
    actividad: PropTypes.arrayOf(
      PropTypes.object,
    ),
    rubro_id: PropTypes.number,
    tamano: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    contacto: PropTypes.string,
    score: PropTypes.number,
    pais: PropTypes.object,
  }).isRequired,
};
export default PosicionDeudorInfo;
